/**
 * Session-scoped tombstone guard for locally deleted documents.
 *
 * THE RACE THIS CLOSES (found by adversarial review of the subpages feature):
 * deleting a document whose tab is mounted with a pending autosave debounce
 * let the tab's flush-on-unmount call saveDocumentBody AFTER deleteNodes had
 * tombstoned the rows — resurrecting an orphaned, invisible `::body` record
 * that never appears in listTree, cannot be reopened, and counts against the
 * 25k per-scope sync row cap forever. Reordering close-before-delete alone
 * cannot fix it: closeDocumentTab only *schedules* the unmount, and the flush
 * runs asynchronously from its cleanup effect, so no call ordering in the
 * sidebar can sequence the flush against the delete.
 *
 * So the guard is SYNCHRONOUS and lives at the store choke point instead:
 * deleteNodes marks every document id here BEFORE its first await, and every
 * document save path (saveDocumentBody, saveDocumentTitle — and through them
 * the unmount flush, pending debounce timers, the AI apply path, and the
 * outbox's closed-doc rewrite) refuses ids that are marked. Any save that
 * begins after deleteNodes was INVOKED — not merely after it finished — is a
 * no-op.
 *
 * Keyed by document id alone: ids are uuid-shaped and unique across scopes in
 * practice, and a false positive would require a cross-scope id collision. The
 * set lives for the session — there is no undelete (tombstones are permanent
 * from the client's point of view). `unmarkDocsDeleted` exists for exactly
 * one case: a delete that FAILED mid-subtree lifts the marks of the docs it
 * never tombstoned, so the still-visible rows stay saveable.
 *
 * SCOPE, honestly: this guards LOCAL deletes only. A teammate deleting a
 * team doc on another device while this one holds pending edits is the same
 * race arriving over sync, and nothing marks it here — that pre-existing
 * class needs a store-level tombstone probe to close (known, accepted).
 */

const deletedDocIds = new Set();

/** Mark document ids as locally deleted. Call BEFORE the first tombstone write. */
export function markDocsDeleted(ids) {
  for (const id of ids) {
    if (id != null) deletedDocIds.add(String(id));
  }
}

/** True when a local delete for this document has begun (or completed). */
export function isDocDeleted(id) {
  return deletedDocIds.has(String(id));
}

/** Lift marks for docs a FAILED delete never actually tombstoned. */
export function unmarkDocsDeleted(ids) {
  for (const id of ids) {
    if (id != null) deletedDocIds.delete(String(id));
  }
}

/** Test-only: reset the session set so cases stay independent. */
export function __clearDeletedDocsForTests() {
  deletedDocIds.clear();
}
